import React from 'react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { UserPlus, CheckCircle, AlertTriangle, Loader2, X, AlertCircle, Shield, FileText, CreditCard, Mail, Users, Building, Phone } from 'lucide-react';

// Sélecteur unifié d'activités (comme dans RegisterPage)
import UnifiedActivitySelector from '../UnifiedActivitySelector';
import { formatCodeNAF } from '../../utils/codeNaf';

// Auto-complétion SIRET (composant canonique app-wide)
import SiretInput from '../ui/SiretInput';

// 🔴 CONFIGURATION CENTRALISÉE - SOURCE UNIQUE
import { INDICATIFS_TELEPHONIQUES, STATUTS_JURIDIQUES, PAYS_LIST, getPaysByCode, getFlagUrl } from '../../config/membreFormConfig';

export default function MembreFormDialog({ ctx }) {
  const { creating, _documents, formData, formErrors, getTitle, handleSiretAutoComplete, handleSubmit, onOpenChange, open, phoneValidation, renderDocumentUploadField, resetForm, selectedIndicatif, territoires, setFormData } = ctx;
  return (
      <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) resetForm(); }}>
        <DialogContent className="max-w-3xl max-h-[95vh] overflow-y-auto p-0 gap-0">
          {/* Header gradient ultra pro */}
          <div className="px-6 pt-5 pb-4 bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-600 text-white relative overflow-hidden">
            <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, white 0%, transparent 50%)' }} />
            <DialogHeader className="relative space-y-2">
              <DialogTitle className="flex items-center gap-3 text-white">
                <div className="h-12 w-12 rounded-2xl bg-white/20 backdrop-blur-sm border-2 border-white/30 flex items-center justify-center shadow-lg flex-shrink-0">
                  <UserPlus className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-lg font-bold leading-tight">{getTitle()}</p>
                  <p className="text-xs text-white/80 mt-0.5">Remplissez les informations pour créer un compte membre</p>
                </div>
              </DialogTitle>
            </DialogHeader>
          </div>

          <div className="px-6 py-5 space-y-5">
            {/* Section IDENTITÉ */}
            <div className="rounded-xl border border-blue-100 bg-gradient-to-br from-blue-50/30 to-white overflow-hidden">
              <div className="px-4 py-2.5 bg-gradient-to-r from-blue-50 to-indigo-50/40 border-b border-blue-100 flex items-center gap-2">
                <Users className="h-4 w-4 text-blue-600" />
                <span className="text-xs font-bold text-blue-700 uppercase tracking-wider">Identité du membre</span>
              </div>
              <div className="p-4 space-y-3">
                {/* Nom + Prénom */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="nom" className="text-xs font-semibold">Nom *</Label>
                    <Input
                      id="nom"
                      value={formData.nom}
                      onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                      className={`h-9 mt-1 ${formErrors.nom ? 'border-red-500' : ''}`}
                      placeholder="Dupont"
                      required
                    />
                    {formErrors.nom && <p className="text-xs text-red-500 mt-1">{formErrors.nom}</p>}
                  </div>
                  <div>
                    <Label htmlFor="prenom" className="text-xs font-semibold">Prénom *</Label>
                    <Input
                      id="prenom"
                      value={formData.prenom}
                      onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                      className={`h-9 mt-1 ${formErrors.prenom ? 'border-red-500' : ''}`}
                      placeholder="Jean"
                      required
                    />
                    {formErrors.prenom && <p className="text-xs text-red-500 mt-1">{formErrors.prenom}</p>}
                  </div>
                </div>

                {/* Email */}
                <div>
                  <Label htmlFor="email" className="text-xs font-semibold">Email *</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className={`pl-10 h-9 ${formErrors.email ? 'border-red-500' : ''}`}
                      placeholder="email@exemple.com"
                      required
                    />
                  </div>
                  {formErrors.email && <p className="text-xs text-red-500 mt-1">{formErrors.email}</p>}
                </div>

                {/* Téléphone */}
                <div>
                  <Label htmlFor="telephone" className="text-xs font-semibold">Téléphone *</Label>
                  <div className="flex flex-col gap-2 mt-1">
                    <div className="flex gap-2">
                      <Select
                        value={formData.indicatif_pays || '+33'}
                        onValueChange={(value) => setFormData({ ...formData, indicatif_pays: value, telephone: '' })}
                      >
                        <SelectTrigger className="w-[155px] h-9">
                          <SelectValue>
                            <span className="flex items-center gap-2">
                              <img
                                src={getFlagUrl(selectedIndicatif.flag)}
                                alt={selectedIndicatif.name}
                                className="w-6 h-4 object-cover rounded-sm"
                              />
                              <span>{selectedIndicatif.code}</span>
                            </span>
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {INDICATIFS_TELEPHONIQUES.map((ind, idx) => (
                            <SelectItem key={`${ind.code}-${ind.flag}-${idx}`} value={ind.code}>
                              <span className="flex items-center gap-2">
                                <img
                                  src={getFlagUrl(ind.flag)}
                                  alt={ind.name}
                                  className="w-6 h-4 object-cover rounded-sm"
                                />
                                <span>{ind.code} {ind.name}</span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="flex-1 relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          id="telephone"
                          className={`w-full pl-10 h-9 ${!phoneValidation.isValid && phoneValidation.currentLength > 0 ? 'border-red-500' : phoneValidation.isValid && phoneValidation.currentLength > 0 ? 'border-green-500' : ''} ${formErrors.telephone ? 'border-red-500' : ''}`}
                          placeholder={`${phoneValidation.expectedLength} chiffres requis`}
                          value={formData.telephone}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^\d\s]/g, '');
                            setFormData({ ...formData, telephone: value });
                          }}
                          required
                        />
                        {phoneValidation.currentLength > 0 && (
                          <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium ${phoneValidation.isValid ? 'text-green-600' : 'text-red-500'}`}>
                            {phoneValidation.currentLength}/{phoneValidation.expectedLength}
                          </span>
                        )}
                      </div>
                    </div>
                    {(() => {
                      if (phoneValidation.currentLength === 0) return formErrors.telephone ? <p className="text-xs text-red-500">{formErrors.telephone}</p> : null;
                      const diff = phoneValidation.currentLength - phoneValidation.expectedLength;
                      if (diff > 0) {
                        return <p className="text-xs text-red-500 flex items-center gap-1"><X className="h-3 w-3" />Trop de chiffres ({diff} en trop)</p>;
                      } else if (diff < 0) {
                        return <p className="text-xs text-orange-500 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{Math.abs(diff)} chiffre(s) manquant(s)</p>;
                      }
                      return <p className="text-xs text-green-600 flex items-center gap-1"><CheckCircle className="h-3 w-3" />Numéro valide</p>;
                    })()}
                  </div>
                </div>

                {/* Adresse postale — pré-remplie par l'import presse-papier */}
                <div>
                  <Label htmlFor="adresse" className="text-xs font-semibold">Adresse</Label>
                  <Input
                    id="adresse"
                    data-testid="membre-adresse-input"
                    value={formData.adresse || ''}
                    onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                    className="h-9 mt-1"
                    placeholder="N° et rue"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="code_postal" className="text-xs font-semibold">Code postal</Label>
                    <Input
                      id="code_postal"
                      data-testid="membre-code-postal-input"
                      value={formData.code_postal || ''}
                      onChange={(e) => setFormData({ ...formData, code_postal: e.target.value })}
                      className="h-9 mt-1"
                      placeholder="97100"
                    />
                  </div>
                  <div>
                    <Label htmlFor="ville" className="text-xs font-semibold">Ville</Label>
                    <Input
                      id="ville"
                      data-testid="membre-ville-input"
                      value={formData.ville || ''}
                      onChange={(e) => setFormData({ ...formData, ville: e.target.value })}
                      className="h-9 mt-1"
                      placeholder="Pointe-à-Pitre"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Section TYPE DE COMPTE — visuel toggle cards */}
            <div className="rounded-xl border border-purple-100 bg-gradient-to-br from-purple-50/30 to-white overflow-hidden">
              <div className="px-4 py-2.5 bg-gradient-to-r from-purple-50 to-pink-50/40 border-b border-purple-100 flex items-center gap-2">
                <Shield className="h-4 w-4 text-purple-600" />
                <span className="text-xs font-bold text-purple-700 uppercase tracking-wider">Type de compte</span>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, role: 'beneficiaire_pro' })}
                    className={`text-left p-3 rounded-xl border-2 transition-all ${formData.role === 'beneficiaire_pro' ? 'border-purple-500 bg-purple-50 shadow-md' : 'border-gray-200 bg-white hover:border-purple-200 hover:bg-purple-50/30'}`}
                    data-testid="select-type-pro"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Building className={`h-4 w-4 ${formData.role === 'beneficiaire_pro' ? 'text-purple-600' : 'text-gray-400'}`} />
                      <span className={`text-sm font-bold ${formData.role === 'beneficiaire_pro' ? 'text-purple-900' : 'text-gray-700'}`}>Professionnel</span>
                      {formData.role === 'beneficiaire_pro' && <CheckCircle className="h-4 w-4 text-purple-600 ml-auto" />}
                    </div>
                    <p className="text-[11px] text-gray-600 leading-tight">Entreprises et indépendants avec SIRET</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, role: 'beneficiaire_particulier' })}
                    className={`text-left p-3 rounded-xl border-2 transition-all ${formData.role === 'beneficiaire_particulier' ? 'border-blue-500 bg-blue-50 shadow-md' : 'border-gray-200 bg-white hover:border-blue-200 hover:bg-blue-50/30'}`}
                    data-testid="select-type-particulier"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Users className={`h-4 w-4 ${formData.role === 'beneficiaire_particulier' ? 'text-blue-600' : 'text-gray-400'}`} />
                      <span className={`text-sm font-bold ${formData.role === 'beneficiaire_particulier' ? 'text-blue-900' : 'text-gray-700'}`}>Particulier</span>
                      {formData.role === 'beneficiaire_particulier' && <CheckCircle className="h-4 w-4 text-blue-600 ml-auto" />}
                    </div>
                    <p className="text-[11px] text-gray-600 leading-tight">Personnes physiques bénéficiant des garanties</p>
                  </button>
                </div>
              </div>
            </div>

            {/* Section INFORMATIONS PROFESSIONNELLES */}
            {formData.role === 'beneficiaire_pro' && (
              <div className="rounded-xl border border-indigo-100 bg-gradient-to-br from-indigo-50/30 to-white overflow-hidden">
                <div className="px-4 py-2.5 bg-gradient-to-r from-indigo-50 to-purple-50/40 border-b border-indigo-100 flex items-center gap-2">
                  <Building className="h-4 w-4 text-indigo-600" />
                  <span className="text-xs font-bold text-indigo-700 uppercase tracking-wider">Informations professionnelles</span>
                </div>
                <div className="p-4 space-y-3">
                  {/* Raison sociale */}
                  <div>
                    <Label htmlFor="raison_sociale" className="text-xs font-semibold">Raison sociale *</Label>
                    <div className="relative mt-1">
                      <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        id="raison_sociale"
                        placeholder="Nom de l'entreprise (auto-complété par SIRET)"
                        value={formData.raison_sociale}
                        onChange={(e) => setFormData({ ...formData, raison_sociale: e.target.value })}
                        className={`pl-10 h-9 ${formErrors.raison_sociale ? 'border-red-500' : ''}`}
                      />
                    </div>
                    {formErrors.raison_sociale && <p className="text-xs text-red-500 mt-1">{formErrors.raison_sociale}</p>}
                  </div>

                  {/* SIRET avec auto-complétion INSEE */}
                  <div>
                    <Label htmlFor="siret" className="text-xs font-semibold flex items-center gap-1.5">
                      SIRET (14 chiffres) *
                      <span className="text-[10px] font-medium px-1.5 py-0 rounded bg-emerald-100 text-emerald-700 border border-emerald-200">Auto INSEE</span>
                    </Label>
                    <div className="mt-1">
                      <SiretInput
                        value={formData.siret}
                        onChange={(value) => setFormData((prev) => ({ ...prev, siret: (value || '').replace(/\D/g, '').slice(0, 14) }))}
                        onAutoComplete={handleSiretAutoComplete}
                        showLabel={false}
                        required
                        placeholder="14 chiffres — auto-complétion INSEE"
                        className={formErrors.siret ? 'border-red-500' : ''}
                      />
                    </div>
                    {formErrors.siret && <p className="text-xs text-red-500 mt-1">{formErrors.siret}</p>}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="statut_juridique" className="text-xs font-semibold">Statut Juridique *</Label>
                      <Select
                        value={formData.statut_juridique}
                        onValueChange={(value) => setFormData({ ...formData, statut_juridique: value })}
                      >
                        <SelectTrigger className={`h-9 mt-1 ${formErrors.statut_juridique ? 'border-red-500' : ''}`}>
                          <SelectValue placeholder="Sélectionner..." />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUTS_JURIDIQUES.map((s) => (
                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {formErrors.statut_juridique && <p className="text-xs text-red-500 mt-1">{formErrors.statut_juridique}</p>}
                    </div>

                    <div>
                      <Label htmlFor="code_naf" className="text-xs font-semibold">Code NAF *</Label>
                      <Input
                        id="code_naf"
                        placeholder="Ex: 43.99C"
                        value={formData.code_naf}
                        onChange={(e) => setFormData({ ...formData, code_naf: e.target.value.toUpperCase() })}
                        onBlur={(e) => setFormData({ ...formData, code_naf: formatCodeNAF(e.target.value) })}
                        maxLength={6}
                        className={`h-9 mt-1 font-mono ${formErrors.code_naf ? 'border-red-500' : ''}`}
                      />
                      <p className="text-[10px] text-slate-500 mt-1">Format : XX.XXY (ex: 64.12Z)</p>
                      {formErrors.code_naf && <p className="text-xs text-red-500 mt-1">{formErrors.code_naf}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="territoire" className="text-xs font-semibold">Territoire d&apos;exercice *</Label>
                      <Select
                        value={formData.territoire}
                        onValueChange={(value) => setFormData({ ...formData, territoire: value })}
                      >
                        <SelectTrigger className={`h-9 mt-1 ${formErrors.territoire ? 'border-red-500' : ''}`}>
                          <SelectValue placeholder="Sélectionner un territoire" />
                        </SelectTrigger>
                        <SelectContent>
                          {territoires.filter(t => t.nom && t.nom.trim() !== '').map((t) => (
                            <SelectItem key={t.id} value={t.nom}>
                              {t.nom}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {formErrors.territoire && <p className="text-xs text-red-500 mt-1">{formErrors.territoire}</p>}
                    </div>

                    <div>
                      <Label htmlFor="pays" className="text-xs font-semibold">Pays *</Label>
                      <Select value={formData.pays} onValueChange={(value) => setFormData({ ...formData, pays: value })}>
                        <SelectTrigger className="h-9 mt-1">
                          <SelectValue>
                            {(() => {
                              const pays = getPaysByCode(formData.pays);
                              return (
                                <span className="flex items-center gap-2">
                                  <img src={getFlagUrl(pays.flag)} alt={pays.name} className="w-5 h-4 object-cover rounded-sm" />
                                  <span>{pays.name}</span>
                                </span>
                              );
                            })()}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {PAYS_LIST.map((pays) => (
                            <SelectItem key={pays.code} value={pays.code}>
                              <span className="flex items-center gap-2">
                                <img src={getFlagUrl(pays.flag)} alt={pays.name} className="w-5 h-4 object-cover rounded-sm" />
                                <span>{pays.name}</span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Sélection de l'activité BTP */}
                  <div className="pt-2">
                    <UnifiedActivitySelector
                      value={formData.activite_professionnelle}
                      onChange={(value) => setFormData({ ...formData, activite_professionnelle: value })}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* 📁 DOCUMENTS REQUIS - Professionnels */}
            {formData.role === 'beneficiaire_pro' && (
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 space-y-4">
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-amber-700" />
                  <h3 className="font-semibold text-amber-900">Documents requis (Professionnel)</h3>
                </div>
                <p className="text-sm text-amber-700">
                  Ces documents seront associés au dossier du membre. Formats acceptés : PDF, JPG, PNG (max 5 MB).
                </p>
                
                <div className="space-y-3">
                  {renderDocumentUploadField(
                    "kbis_inpi",
                    "Extrait KBIS ou INPI",
                    "Document officiel d'immatriculation de l'entreprise",
                    true
                  )}
                  {renderDocumentUploadField(
                    "piece_identite_gerant",
                    "Pièce d'identité du gérant",
                    "Passeport ou Carte d'identité du dirigeant",
                    true
                  )}
                  {renderDocumentUploadField(
                    "attestation_ftpe",
                    "Attestation Adhésion FTPE OUTREMER",
                    "Document d'adhésion à la fédération (si applicable)",
                    false
                  )}
                </div>
              </div>
            )}

            {/* 📁 DOCUMENTS REQUIS - Particuliers */}
            {formData.role === 'beneficiaire_particulier' && (
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 space-y-4">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-purple-700" />
                  <h3 className="font-semibold text-purple-900">Document requis (Particulier)</h3>
                </div>
                <p className="text-sm text-purple-700">
                  Ce document sera associé au dossier du membre. Formats acceptés : PDF, JPG, PNG (max 5 MB).
                </p>
                
                <div className="space-y-3">
                  {renderDocumentUploadField(
                    "piece_identite",
                    "Pièce d'identité",
                    "Passeport ou Carte d'identité nationale",
                    true
                  )}
                </div>
              </div>
            )}

            {/* Info box - Mot de passe automatique */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-900">Mode email simulé</p>
                  <p className="text-sm text-blue-700">
                    Un mot de passe temporaire sera généré automatiquement. 
                    Les identifiants de connexion seront affichés à l&apos;écran après la création.
                    Vous devrez les transmettre manuellement au membre.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => { onOpenChange(false); resetForm(); }}>
              Annuler
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={creating}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {creating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Création en cours...
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4 mr-2" />
                  CRÉER LE MEMBRE
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
  );
}
